import React from "react";
import { Assign, DeletePanel, ModuleDropDown, MyPaper, SectionPaper, SelectBox, TagsReactSelect, TextField, UnAssign } from "../../../components";
import { Box, Button, Grid2 as Grid, Stack, Tooltip, Typography } from "@mui/material";
import { useAppDispatch, useAppSelector } from "../../../redux";
import { ReactSelectOption, SelectBoxOption, TagInfo } from "../../../interfaces";
import { resetPricingSlice, updateErrorObj, updatePricingByKeyValue, updatePricingSliceByKeyValue } from "../../../redux/slices/pricing";
import { BOMSliceInterface } from "../../../redux/slices/bom";
import { AssignmentStatus, useCreatePricingMutation, useLazyGetAllCostingQuery, useLazyGetBOMsQuery, useLazyGetBOMTreeByIdQuery, useUpdateAssignmentMutation, useUpdatePricingMutation } from "../../../redux/services";
import { chartOptions, mapTreeToPriceCost, PricingModelOptions } from "../utils";
import { AppRoutes } from "../../../router/routes";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { LoadingButton } from "@mui/lab";
import copy from "fast-copy";
import { closeBackdrop, openBackdrop } from "../../../redux/slices/backdrop";
import { useSnackbar } from "notistack";
import { CostGroup, Costing, ExchangeData, formatCurrency } from "pdf-generator";
import { PublishedIcon } from "../../../images/svg/publishedIcon";
import { WIPIcon } from "../../../images/svg/WIPIcon";
import { closeConfirmationDialog, openConfirmationDialog } from "../../../redux/slices/confirmationDialog";
import { closeDialog, openDialog } from "../../../redux/slices/dialog";
import { PublishComponent } from "../../materials/dashboard";
import moment from "moment";
import { ExchangeRatesSelection } from "../exchangeRatesSelection";
import Chart from "react-google-charts";
import { entityOptions } from "../../viewQuotes/itemDetails/utils";
import { getUserSelectedCurrency, TAG_CANONICAL_NAMES } from "../../../utils";
import { PERMISSIONS } from "../../../router/permission";
import { HasAccess } from "../../../router/authorization";

export const BasicDetail: React.FC<{ savedPricing: any, refetch: () => void, readOnly: boolean }> = ({ refetch, readOnly }) => {

    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const { id = "New" } = useParams();

    const isEdit = id !== "New";

    const { enqueueSnackbar } = useSnackbar();

    const { pricing, auth } = useAppSelector(store => store);

    const { perm } = auth.userDetails;

    const DEFAULT_CURRENCY = getUserSelectedCurrency(auth.userPreferences).value;

    const publishable = React.useMemo(() => HasAccess(perm, PERMISSIONS.COMMERCIAL_PRICING_PUBLISH), [perm]);

    const [getBOMs, getBOMsData] = useLazyGetBOMsQuery();
    const [getAllCosting, costingRes] = useLazyGetAllCostingQuery();
    const [createPricing, createRes] = useCreatePricingMutation();
    const [updatePricing, updateRes] = useUpdatePricingMutation();
    const [getBOMTree] = useLazyGetBOMTreeByIdQuery();
    const [assignSequenceNumberQuery] = useUpdateAssignmentMutation();

    const [bomCostingDetails, setDetails] = React.useState<SelectBoxOption[]>([]);

    const costing = React.useMemo(() => {
        const { data = [] } = costingRes;
        const latestCosting = data?.filter((d: any) => new Date(d.created_time) > new Date(pricing.modified_time));
        const mostRecentCosting: Costing = latestCosting?.reduce((recent: any, current: any) => {
            return new Date(current.created_time) > new Date(recent.created_time) ? current : recent;
        }, latestCosting[0] || {});
        let masterData: any[] = [];
        let levelsData: any[] = [];
        const base_currency = mostRecentCosting?.pricing_config?.exchange_rates?.base_currency;
        if (Object.keys(mostRecentCosting).length > 0) {
            // Master Data Manipulation
            const includeMasterFields = [
                'total_bom_cost',
                'total_other_cost',
                'total_discount',
                'total_tax'
            ];

            if (mostRecentCosting?.margin_amount) {
                includeMasterFields.push("margin_amount");
            }

            const masterLabels: Record<any, string> = {
                'total_bom_cost': `Major Items Cost (${base_currency})`,
                'total_other_cost': `Other Items Cost (${base_currency})`,
                'total_discount': `Adjustments Cost (${base_currency})`,
                'total_tax': `Taxes (${base_currency})`,
            };

            if (mostRecentCosting?.margin_amount) {
                masterLabels["margin_amount"] = `Margin (${base_currency})`;
            }

            masterData = [
                ["Pricing Breakup", "Value"],
                ...(includeMasterFields.map((key: string) => {
                    if (key === includeMasterFields[0]) {
                        // eslint-disable-next-line
                        return [masterLabels[key], Number((Number(eval(`mostRecentCosting?.${key}`)) + mostRecentCosting?.total_cost_group_cost).toFixed(2))]
                    }
                    // eslint-disable-next-line
                    return [masterLabels[key], Number(Math.abs(eval(`mostRecentCosting?.${key}`)).toFixed(2))]
                }) ?? [])
            ]

            // // Levels Data Manipulation
            // levelsData.push(["BOM First Level Details", "Value"]);

            // mostRecentCosting?.calculated_cost?.children_costs?.forEach((c: ChildrenCost) => {
            //     levelsData.push([`${c.sequence_id}-r${c.version} (${c.short_description})`, Number(c.final_total_cost_in_pricing_currency?.cost?.toFixed(2))])
            // })

            // mostRecentCosting?.calculated_cost?.material_costs?.forEach((c: MaterialCost) => {
            //     levelsData.push([`${c.sequence_id}-r${c.version} (${c.short_description})`, Number(c.final_total_cost_in_pricing_currency?.cost?.toFixed(2))])
            // })
        }

        return { masterData, levelsData, base_currency }
        // eslint-disable-next-line
    }, [costingRes.status, pricing?.modified_time])

    const updateState = (key: string, value: string | SelectBoxOption | null) => {
        dispatch(updatePricingSliceByKeyValue({ parentKey: 'basic_details', childKey: key, value }))
    }

    const loadOptions = async (search: string, loadedOptions: any, object_type: string, object_id: string) => {
        try {
            let page = Math.round(loadedOptions.length / 50);
            let options: { data: BOMSliceInterface[] } = { data: [] };
            options = await getBOMs({ page, pageSize: 50, search: search.trim(), object_id: object_id, object_type: object_type, include_histories: true }).unwrap();
            return { options: options.data?.map((_: any) => ({ ..._, value: _._id, label: `${_.sequence_id}-r${_?.version ?? 0} (${_?.short_description ?? ""})` })), hasMore: options.data.length === 50 }
        } catch (error) {
            console.error('error:', error)
            return { options: [], hasMore: false }
        }
    };

    const updatePublishStatus = () => {
        dispatch(
            openConfirmationDialog({
                title: "Publish Operation",
                body: <DeletePanel
                    message={`Are you sure you want to publish the pricing ${pricing.sequenceId}?`}
                />,
                positiveBtn: "Confirm",
                negativeBtn: "Cancel",
                onOk: () => proceedToPublish(),
                onNegativeBtn: () => dispatch(closeConfirmationDialog())
            })
        );
    };

    const proceedToPublish = () => {
        dispatch(closeConfirmationDialog());
        dispatch(openDialog({
            title: "",
            body: <PublishComponent {...pricing} _id={pricing.id} sequence_id={pricing.sequenceId ?? ""} refetch={refetch} object_type="bomPricing" object_id={pricing.id} />,
            hidePositiveBtn: true,
            hideNegativeBtn: true,
            enablePadding: false
        }));
    };

    const openCalculateCost = () => {
        dispatch(openDialog({
            title: "Update Exchange Rates",
            body: <ExchangeRatesSelection onCalculate={(exchange_rates: ExchangeData) => {
                dispatch(updatePricingByKeyValue({ key: "exchange_rates", value: exchange_rates }))
                if (isEdit) {
                    onCreateUpdateBtnClicked(exchange_rates)
                }
            }} />,
            hideNegativeBtn: true,
            hidePositiveBtn: true,
            maxWidth: 'lg',
            enablePadding: false
        }))
    };

    const generateSerialNumbersForTemplate = async (id: string | null, serial_num: string, type: AssignmentStatus = "assign", isManual?: boolean) => {
        dispatch(openBackdrop(`${type === "assign" ? "Assigning" : "Un Assigning"} Serial Number....`));
        let object_type = `bomPricing`;
        let object_id = pricing.id;

        await assignSequenceNumberQuery({
            id: id,
            status: type,
            manual_serial_num: isManual,
            payload: [
                {
                    object_type: object_type,
                    object_id: object_id,
                    serial_num: (type === "unassign" || isManual || serial_num) ? serial_num : undefined,
                },
            ],
        }).then(async (res: any) => {
            if (res.error) {
                enqueueSnackbar(`Something went wrong unable to ${type === 'assign' ? "assign" : "un-assign"} serial number. Info: ${res?.error?.data?.internal_error ?? res?.error?.data?.title ?? "-"}`, { variant: "error" })
            } else {
                enqueueSnackbar(`${type === 'assign' ? "Assigned" : "Un Assigned"} Serial Number`, { variant: "success" });
                dispatch(closeDialog());
                refetch();
            }
        }).catch((err: any) => {
            console.error('err:', err)
            enqueueSnackbar(err?.data?.internal_error ?? err?.data?.title ?? 'Something went wrong unable to assign serial number', { variant: "error" });
        });
        dispatch(closeBackdrop());
    };

    const onAssignClick = () => {
        dispatch(
            openDialog({
                title: "",
                hideNegativeBtn: true,
                hidePositiveBtn: true,
                enablePadding: false,
                body: <Assign onAssign={(id?: string | null, serial_num?: string, isManual?: boolean, type: AssignmentStatus = "assign") => generateSerialNumbersForTemplate(id ?? "", serial_num ?? "", type, isManual)} />,
            })
        );
    };

    const onUnAssignClick = () => {
        dispatch(
            openDialog({
                title: "",
                hideNegativeBtn: true,
                hidePositiveBtn: true,
                body: (
                    <UnAssign
                        onCancel={() => dispatch(closeDialog())}
                        onUnassign={() => generateSerialNumbersForTemplate(null, pricing?.serial_num ?? "", "unassign")}
                    />
                ),
            })
        );
    };

    const validate = () => {
        let isValid = true;
        const errorObj = copy(pricing.error.basic_details);
        if (!pricing.basic_details?.pricing_name?.trim()) {
            isValid = false;
            errorObj.pricing_name = true;
        }

        if (!pricing.basic_details?.bom_id) {
            isValid = false;
            errorObj.bom_id = true;
        }

        if (!pricing.basic_details?.pricing_model) {
            isValid = false;
            errorObj.pricing_model = true;
        }

        if (!pricing.basic_details?.object_type) {
            isValid = false;
            errorObj.object_type = true;
        }

        if (!pricing.basic_details?.object_id) {
            isValid = false;
            errorObj.object_id = true;
        } else {
            errorObj.object_id = false;
        }

        if (!pricing.exchange_rates?.base_currency) {
            isValid = false;
            enqueueSnackbar("Please choose the currency and exchange rates", { variant: "warning" });
        }

        dispatch(updateErrorObj({ basic_details: errorObj }))

        return isValid;
    }

    const onCreateUpdateBtnClicked = async (e_rates?: ExchangeData) => {
        const {
            basic_details,
            cost_groups = [],
            other_costs = [],
            discounts = [],
            taxes = [],
            exchange_rates,
            bom_cost_quantity = 1,
            bom_cost_discount_percentage = 0,
            margin_ratio = 0,
        } = pricing;
        if (validate()) {
            dispatch(openBackdrop(isEdit ? "Updating Pricing..." : "Creating Pricing..."));
            const tags_info: Partial<TagInfo>[] = [];

            if (basic_details.priority) {
                tags_info.push({
                    tag_group_id: basic_details.priority.tag_group_id as string,
                    tag_id: basic_details.priority.value as string
                })
            }

            if (isEdit) {
                const bom_id = basic_details?.bom_id?._id as string;
                await getBOMTree({ id: bom_id }).unwrap().then(async (res) => {
                    const bom_cost = mapTreeToPriceCost(res);

                    const unlinks = pricing?.associations?.map(a => ({ object_type: a?.object_type, object_ids: a?.object_details?.map(o => o?._id) })) ?? [];

                    await updatePricing({
                        pricing_id: id,
                        bom_id: basic_details?.bom_id?._id as string,
                        payload: {
                            bom_cost: bom_cost,
                            // bom_id: basic_details?.bom_id?._id as string,
                            pricing_name: basic_details.pricing_name,
                            pricing_model: basic_details?.pricing_model?.value,
                            remarks: basic_details?.remarks,
                            exchange_rates: e_rates || exchange_rates,
                            margin_ratio,
                            cost_groups: cost_groups?.map((c: CostGroup, idx: number) => ({
                                position: idx + 1,
                                title: c.title,
                                quantity: Number(c.quantity) || 1,
                                discount_percentage: Number(c.discount_percentage) || 0,
                                total_amount: Number(c.total_amount) || 0,
                                // value: c.value,
                                costs: c.costs?.map((cost, idx: number) => ({
                                    position: idx + 1,
                                    description: cost.description,
                                    units: cost.units,
                                    value: Number(cost.value) || 0,
                                    quantity: Number(cost.quantity) || 0,
                                    total_cost: Number(cost.total_cost) || 0
                                })) ?? []
                            })) ?? [],
                            other_costs: other_costs,
                            discounts: discounts,
                            taxes: taxes,
                            bom_cost_quantity,
                            bom_cost_discount_percentage,
                            links: [{ object_type: basic_details.object_type?.value as string, object_ids: [basic_details.object_id?.value] }],
                            unlinks,
                            tags_info
                        }
                    })
                        .unwrap()
                        .then((res) => {
                            enqueueSnackbar(res.message, { variant: "success" });
                            fetchCosting();
                            refetch();
                        })
                        .catch((error) => {
                            enqueueSnackbar(error?.data?.title ?? "Unable to update the pricing", { variant: "error" });
                            console.error(`Error updating pricing: ${JSON.stringify(error)}`)
                        })
                        .finally(() => {
                            dispatch(closeBackdrop());
                        })

                })
                    .catch((error) => {
                        enqueueSnackbar(error?.data?.title ?? "Unable to update the pricing", { variant: "error" });
                        console.error(`Error updating pricing: ${JSON.stringify(error)}`);
                        dispatch(closeBackdrop());
                    });
            } else {
                const bom_id = basic_details?.bom_id?._id as string;
                await getBOMTree({ id: bom_id }).unwrap().then(async (res) => {
                    const bom_cost = mapTreeToPriceCost(res);

                    await createPricing({
                        bom_id: bom_id,
                        payload: {
                            bom_cost: bom_cost,
                            exchange_rates: exchange_rates,
                            pricing_name: basic_details.pricing_name,
                            pricing_model: basic_details?.pricing_model?.value,
                            remarks: basic_details?.remarks,
                            cost_groups: [],
                            other_costs: [],
                            discounts: [],
                            taxes: [],
                            bom_cost_discount_percentage: 0,
                            margin_ratio: 0,
                            bom_cost_quantity: 1,
                            links: [{ object_type: basic_details.object_type?.value as string, object_ids: [basic_details.object_id?.value] }],
                            tags_info
                        }
                    })
                        .unwrap()
                        .then((res) => {
                            enqueueSnackbar(res.message, { variant: "success" });
                            refetch();
                            let newPricingId = res?._id ?? "New";
                            // updateState("matId", newMatId);
                            // updateState("sequenceId", createMaterialData.data?.sequence_id);
                            navigate(location.pathname.replace("New", newPricingId));

                        })
                        .catch((error) => {
                            enqueueSnackbar("Unable to create the pricing", { variant: "error" });
                            console.error(`Error creating pricing: ${JSON.stringify(error)}`)
                        })
                        .finally(() => {
                            dispatch(closeBackdrop());
                        })

                })
                    .catch((error) => {
                        enqueueSnackbar("Unable to create the pricing", { variant: "error" });
                        console.error(`Error creating pricing: ${JSON.stringify(error)}`);
                        dispatch(closeBackdrop());
                    });
            }
        }
    }

    const fetchCosting = () => {
        if (pricing.basic_details?.bom_id?.value && isEdit) {
            getAllCosting({ bom_id: pricing.basic_details?.bom_id?.value as string, pricing_id: id });
        }
    }

    React.useEffect(() => {
        const { data = [] } = costingRes;
        const latestCosting = data?.filter((d: any) => new Date(d.created_time) > new Date(pricing.modified_time));
        const costing: Costing = latestCosting?.reduce((recent: any, current: any) => {
            return new Date(current.created_time) > new Date(recent.created_time) ? current : recent;
        }, latestCosting[0] || {});
        setDetails([]);
        if (Object.keys(costing).length > 0) {
            const base_currency = costing.pricing_config?.exchange_rates?.base_currency ?? DEFAULT_CURRENCY;
            setDetails([
                {
                    label: `Cost Breakup for: `,
                    value: `${costing?.calculated_cost?.short_description} (${costing?.calculated_cost?.sequence_id ?? ""
                        }-r${costing?.calculated_cost?.version ?? 0})`,
                },
                {
                    label: `Total Cost (after tax): `,
                    value: `${formatCurrency(costing?.final_price ?? 0, base_currency, false)}`,
                },
                {
                    label: `Calculated By: `,
                    value: `${costing?.created_user}`,
                },
                {
                    label: `Calculated At: `,
                    value: `${moment(costing?.created_time).format("lll")}`,
                },
                {
                    label: `Pricing ID: `,
                    value: pricing?.sequenceId ?? "",
                }
            ]);
        }
        // eslint-disable-next-line
    }, [costingRes.status, pricing.modified_time, DEFAULT_CURRENCY])

    React.useEffect(() => {
        fetchCosting();
        // eslint-disable-next-line
    }, [pricing.modified_time])

    return (
        (<Stack spacing={2}>
            <SectionPaper title="Basic Details" hideExpandCollapse={true} renderButtons={[
                pricing?.released
                    ? <Stack direction={"row"} sx={{
                        alignItems: "center"
                    }}>
                        <Typography variant="body2" color="textSecondary">Status: </Typography>
                        <Button disableRipple disableFocusRipple sx={{ cursor: "default", '&:hover': { backgroundColor: 'transparent' } }}>
                            <Tooltip
                                title="Published"
                                children={<Box
                                    sx={{
                                        width: 30,
                                        height: 30,
                                        alignItems: "center",
                                        justifyContent: "center"
                                    }}><PublishedIcon sx={{ width: "100%", height: "100%" }} /></Box>}
                            />
                        </Button>
                    </Stack>
                    : isEdit ? <Stack direction={"row"} sx={{
                        alignItems: "center"
                    }}>
                        <Typography variant="body2" color="textSecondary">Status: </Typography>
                        <Button disableRipple disableFocusRipple><Tooltip title={publishable ? "Work in progress, click to publish the item" : "Not Published"} children={<Box
                            sx={{
                                width: 30,
                                height: 30,
                                alignItems: "center",
                                justifyContent: "center"
                            }}><WIPIcon sx={{ width: "100%", height: "100%" }} onClick={() => publishable ? updatePublishStatus() : false} /></Box>} /></Button>
                    </Stack>
                        : <></>
            ]}>
                <Grid container spacing={3}>
                    <Grid
                        size={{
                            xs: 12,
                            sm: 12,
                            md: 6
                        }}>
                        <SelectBox
                            id={"pricing-pricing-model"}
                            label={"Pricing Model"}
                            isRequired
                            placeholder={"Select Pricing Model"}
                            value={pricing.basic_details.pricing_model}
                            options={PricingModelOptions}
                            onChange={(data: ReactSelectOption) => updateState("pricing_model", data)}
                            error={pricing.error.basic_details.pricing_model}
                            helperText={pricing.error.basic_details.pricing_model ? 'Please select pricing model' : ''}
                            isMenuFixed
                            viewOnly={readOnly}
                        />
                    </Grid>

                    <Grid
                        size={{
                            xs: 12,
                            sm: 12,
                            md: 6
                        }}>
                        <SelectBox
                            id="pricing-object-type"
                            label={"Entity Type"}
                            isRequired
                            value={pricing.basic_details.object_type}
                            options={entityOptions}
                            onChange={(data: ReactSelectOption) => {
                                dispatch(updatePricingByKeyValue({ key: "basic_details", value: { ...pricing.basic_details, object_type: data, object_id: null, bom_id: null } }))
                            }}
                            error={pricing.error.basic_details.object_type}
                            helperText={pricing.error.basic_details.object_type ? 'Please select the entity type' : ''}
                            viewOnly={readOnly}
                        />
                    </Grid>

                    <Grid
                        size={{
                            xs: 12,
                            sm: 12,
                            md: 6
                        }}>
                        <ModuleDropDown
                            label={'Entity'}
                            entity={(pricing.basic_details.object_type?.value ?? "none") as any}
                            value={pricing.basic_details.object_id as SelectBoxOption}
                            disabled={!pricing.basic_details.object_type?.value}
                            onChange={(option) => {
                                dispatch(updatePricingByKeyValue({ key: "basic_details", value: { ...pricing.basic_details, object_id: option, bom_id: null } }))
                            }}
                            isRequired
                            error={pricing.error.basic_details.object_id}
                            helperText={pricing.error.basic_details.object_id ? 'Please select the entity' : ''}
                            viewOnly={readOnly}
                        />
                    </Grid>

                    <Grid
                        size={{
                            xs: 12,
                            sm: 12,
                            md: 6
                        }}>
                        <SelectBox
                            id={"pricing-bom-options"}
                            key={(pricing.basic_details.object_id?.value ?? "none") as any}
                            isPaginate
                            loadOptions={(search: string, loadedOptions: any) => loadOptions(search, loadedOptions, pricing.basic_details.object_type?.value as string, pricing.basic_details.object_id?.value as string)}
                            label={"BOM"}
                            isRequired
                            placeholder={"Select BOM"}
                            value={pricing.basic_details.bom_id}
                            loading={getBOMsData.isLoading}
                            options={[]}
                            onChange={(data: ReactSelectOption) => updateState("bom_id", data)}
                            error={pricing.error.basic_details.bom_id}
                            helperText={pricing.error.basic_details.bom_id ? 'Please select bom' : ''}
                            isDisabled={!pricing.basic_details.object_id?.value}
                            isMenuFixed
                            viewOnly={readOnly}
                        />
                    </Grid>

                    <Grid
                        size={{
                            xs: 12,
                            sm: 12,
                            md: 6
                        }}>
                        <TextField
                            id="pricing-name"
                            fullWidth
                            required
                            sx={{
                                '.MuiOutlinedInput-notchedOutline': {
                                    borderWidth: "1px !important"
                                }
                            }}
                            isLabeled
                            label={"Pricing Name"}
                            size="small"
                            placeholder="Enter Pricing Name"
                            value={pricing.basic_details.pricing_name}
                            onChange={(e) => updateState('pricing_name', e.target.value)}
                            error={pricing.error.basic_details.pricing_name}
                            helperText={pricing.error.basic_details.pricing_name ? 'Please enter Pricing Name' : ''}
                            viewOnly={readOnly}
                        />
                    </Grid>

                    <Grid
                        size={{
                            xs: 12,
                            sm: 12,
                            md: 6
                        }}>
                        <TextField
                            id="pricing-remark"
                            fullWidth
                            isLabeled
                            label={"Remarks"}
                            sx={{
                                '.MuiOutlinedInput-notchedOutline': {
                                    borderWidth: "1px !important"
                                }
                            }}
                            placeholder="Enter Remarks"
                            size="small"
                            value={pricing.basic_details.remarks}
                            onChange={(e) => updateState('remarks', e.target.value)}
                            viewOnly={readOnly}
                        />
                    </Grid>

                    <Grid
                        size={{
                            xs: 12,
                            sm: 12,
                            md: 6
                        }}>
                        <Stack>
                            <SelectBox
                                id={"pricing-exchange-rates"}
                                label={"Currency"}
                                isRequired
                                placeholder={"Select Currency"}
                                value={{ label: pricing?.exchange_rates?.base_currency, value: pricing?.exchange_rates?.base_currency }}
                                options={[{ label: "Update", value: "update" }]}
                                onChange={(data: ReactSelectOption) => openCalculateCost()}
                                isMenuFixed
                                viewOnly={readOnly}
                            />
                            <Typography variant={"caption"}>Last calculated on: {pricing.exchange_rates?.base_currency ? moment(pricing.exchange_rates?.current_date).format("YYYY-MM-DD HH:mm:ss") : "N/A"}</Typography>
                        </Stack>
                    </Grid>

                    {/* Priority */}
                    <Grid
                        size={{
                            xs: 12,
                            sm: 12,
                            md: 6
                        }}>
                        <TagsReactSelect
                            id="pricing-priority"
                            value={pricing.basic_details.priority}
                            onChange={(data: ReactSelectOption) => updateState("priority", data)}
                            isMenuFixed
                            viewOnly={readOnly}
                            canonical_name={TAG_CANONICAL_NAMES.price_priority}
                        />
                    </Grid>

                    {/* Serial Number */}
                    {isEdit && (
                        <Grid size={{
                            xs: 12,
                            sm: 12,
                            md: 6
                        }}>
                            <Stack direction={"row"} alignItems={"center"} justifyContent={"space-between"}>
                                <Stack sx={{ overflowWrap: "break-word" }}>
                                    <Typography sx={readOnly ? { fontFamily: "htrts_medium" } : {}} color={readOnly ? undefined : "primary"} variant={readOnly ? "body1" : "body2"} align="left" gutterBottom>
                                        Custom Reference No.
                                    </Typography>
                                    <Typography variant="body2" sx={{ fontFamily: "htrts_regular" }}>{typeof pricing.serial_num === "string" ? pricing.serial_num : "N/A"}</Typography>
                                </Stack>
                                {(!readOnly && !pricing?.released) &&
                                    <Stack direction={"row"} spacing={2}>
                                        <Button variant={"contained"} size="small" onClick={() => typeof pricing.serial_num === "string" ? onUnAssignClick() : onAssignClick()}>
                                            {typeof pricing.serial_num === "string" ? "Unassign" : "Assign"}
                                        </Button>
                                    </Stack>
                                }
                            </Stack>
                        </Grid>
                    )}

                </Grid>
            </SectionPaper>
            {bomCostingDetails.length > 0 &&
                <Box>
                    <SectionPaper title={"Costing Details"}>
                        <>
                            <Stack gap={1}>
                                {bomCostingDetails.map(detail => (<Stack gap={1} direction={{ sm: "column", md: "row" }}>
                                    <Typography variant="body2">{detail.label}</Typography>
                                    <Typography variant="body2"><b>{detail.value}</b></Typography>
                                </Stack>))}
                            </Stack>
                            <Chart
                                chartType="PieChart"
                                data={costing.masterData}
                                options={{
                                    ...chartOptions,
                                    textColor: 'red',
                                    title: `Master Pricing (${costing?.base_currency})`,
                                    // is3D: true,
                                    // pieHole: 0.85,
                                    // legend: { position: "bottom" },
                                }}
                                width={"100%"}
                                height={"400px"}
                            />
                        </>
                    </SectionPaper>
                </Box>
            }

            {!readOnly &&
                <MyPaper>
                    <>
                        {/* Actions */}
                        <Stack
                            sx={{
                                gap: 2,
                                flexDirection: "row",
                                alignItems: "center",
                                justifyContent: "center"
                            }}>
                            <Button
                                variant="outlined"
                                onClick={() => {
                                    dispatch(resetPricingSlice())
                                    navigate(AppRoutes.pricingList)
                                }}
                                disabled={
                                    createRes.isLoading || updateRes.isLoading
                                }
                            >
                                Cancel
                            </Button>

                            <LoadingButton
                                id="u-c-btn"
                                variant="contained"
                                onClick={(e) => onCreateUpdateBtnClicked()}
                                loading={
                                    createRes.isLoading || updateRes.isLoading
                                }
                            >
                                {`${isEdit ? "Update" : "Create"}`}
                            </LoadingButton>
                        </Stack>
                    </>
                </MyPaper>
            }
        </Stack>)
    );
}